/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import get from 'lodash/get';
import { action, computed, observable, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { GetCheckoutCartInput } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, KeyCodes, Modal, ModalBody } from '@msdyn365-commerce-modules/utilities';
import { AsyncResultStatus, getUrlSync } from '@msdyn365-commerce/core';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { isEmpty } from '@msdyn365-commerce/retail-proxy';
import { Cart, CommerceProperty, GiftCard, SalesOrder } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import CittaTelemetry from '../../common/telemetry/citta-telemetry';
import { laybuyCancelOrderAsync } from '../../data-actions/generated/DataActionExtension.g';
import { IConfiguration, IDXC_CapturePaymentResponse, IDXC_CreateOrderResponse, IDXC_GetConfigurationResponse, IDXC_GetOrderResponse, IGetTransactionEntity, IGetTransactionIdEntity } from '../../data-actions/generated/DataServiceEntities.g';
import { getGiftCardBalanceCheck, GiftCardBalanceCheckInput} from '../../data-actions/gift-card/get-card-balance';
import { AfterpayCapturePaymentDataActionInput, IAfterpayCapturePaymentDataActionAction } from './actions/afterpay-capture-payment-data-action';
import { AfterpayCreateOrderDataActionInput, IAfterpayCreateOrderDataActionAction, IAfterpayCreateOrderInput } from './actions/afterpay-create-order-data-action';
import { AfterpayGetConfigDataActionInput, IAfterpayGetConfigDataActionAction } from './actions/afterpay-get-config-data-action';
import { AfterpayGetOrderDataActionInput, IAfterpayGetOrderDataActionAction } from './actions/afterpay-get-order-data-action';
import emptyActiveCart from './actions/empty-active-cart';
import { confirmOrder as confirmLaybuyOrder } from './actions/laybuy-data-action';
import { getTransactionAction, paymentExpressGetTransactionInput } from './actions/pe-get-transaction-data-action';
import { getTransactionIdAction, paymentExpressInput } from './actions/pe-get-transaction-id-data-action';
import placeOrderAction, { ICheckoutDetailsObject } from './not-actions/place-order.action';
import { ICittaCheckoutPaymentInstrumentData } from './citta-checkout-payment-instrument.data';
import { ICittaCheckoutPaymentInstrumentProps } from './citta-checkout-payment-instrument.props.autogenerated';
import CittaPaymentAfterpayTabContent from './components/citta-payment-afterpay-tab-content';
import CittaPaymentCreditCardTabContent, { ICCMarkupData } from './components/citta-payment-credit-card-tab-content';
import CittaPaymentInstrumentTab from './components/citta-payment-instrument-tab';
import CittaPaymentLaybuyTabContent from './components/citta-payment-laybuy-tab-content';
import { createAddressObject, createDiscountsObject, createOrderItemsObject, stringToBoolean, validateCardExpiry, validateCVC } from './components/citta-payment.utils';
import { ILaybuyButtonData } from './components/LaybuyButton.props';
import withVisibilityObserver, { IVisibilyObserverProps } from './components/with-visibility-observer';
import { paymentStore, showPaymentError } from './payment.store';
import { getExtensionPropsLength, maskGiftCard } from './utils';

export interface ICheckoutPaymentInstrumentModuleProps extends ICittaCheckoutPaymentInstrumentProps<ICittaCheckoutPaymentInstrumentData>, IModuleStateProps, IVisibilyObserverProps { }

const CC_PAYMENT_FAILED_DEFAULT = 'Credit card payment failed, please try again.';
const AP_PAYMENT_ERROR_DEFAULT = 'Afterpay payment error, please try again.';
const AP_PAYMENT_FAILED_DEFAULT = 'Afterpay order failed or was cancelled, please try again.';
const AP_SERVICE_ERROR_DEFAULT = 'There was an error with the Afterpay service, please try again.';

interface ICheckoutPaymentInstrumentState {
    isBusy: boolean;

    ccCvc: string;
    ccExpiryMonth: string;
    ccExpiryYear: number;
    ccName: string;
    ccNumber: string;
    ccSessionId: string;
    cardNameError: boolean;
    cardNumError: boolean;
    cardCvcError: boolean;
    cardExpiryError: boolean;

    afterpayAvailable: boolean;
    afterpayConfiguration: IAfterpayConfigObject | null;
    afterpayMin: number;
    afterpayMax: number;
    afterpayOrderToken: string;
    laybuyAvailable: boolean;

    isProcessingOrder: boolean;
}

export interface ICheckoutPaymentOverlayModal {
    modal: INodeProps;
}

export interface ICheckoutPaymentInstrumentViewProps extends ICittaCheckoutPaymentInstrumentProps<{}>, ICheckoutPaymentInstrumentState {
    isVisible?: boolean;

    paymentMethod: string;
    paymentTabs: React.ReactNode;
    paymentTabContent: React.ReactNode;
    processingModal: React.ReactNode;
}

/**
 *
 * CheckoutPaymentInstrument component
 * @extends {React.Component<ICittaCheckoutPaymentInstrumentProps<ICittaCheckoutPaymentInstrumentData>>}
 */
// @ts-ignore
@withModuleState
@observer
export class CheckoutPaymentInstrument extends React.Component<ICheckoutPaymentInstrumentModuleProps> {
    @computed get asyncResultStatus(): AsyncResultStatus | undefined {
        const isLoading = Object.values(this.props.data).some(data => get(data, 'status') === 'LOADING');
        if (isLoading) {
            return 'LOADING';
        }

        const isSuccess = Object.values(this.props.data).every(data => get(data, 'status') === 'SUCCESS');
        if (isSuccess) {
            return 'SUCCESS';
        }

        const isFailed = Object.values(this.props.data).some(data => get(data, 'status') === 'FAILED');
        if (isFailed) {
            return 'FAILED';
        }

        return;
    }

    /**
     * computes Afterpay instalment amount
     */
    @computed get getAfterpayInstalment(): string {
        return (this.getAmountDue / 4).toFixed(2);
    }

    /**
     * computes Laybuy instalment amount
     */
    @computed get getLaybuyInstalment(): string {
        return (this.getAmountDue / 6).toFixed(2);
    }

    /**
     * Gets current channel currency
     */
    @computed get getCurrency(): string {
        const channelConfiguration = this.props.data.channelConfiguration.result;

        return channelConfiguration!.Currency || '';
    }

    /**
     * returns true if there is still an amount to be paid
     */
    @computed get isAmountDue(): boolean {
        return this.getAmountDue > 0;
    }

    /**
     * Returns the amount left on the total (minus gift cards & loyalty amounts)
     * note we're not using loyalty, but it's here just in case
     */
    @computed get getAmountDue(): number {
        const cart = this.props.data.checkout?.result?.checkoutCart.cart;
        if (!cart || !cart.CartLines || !cart.CartLines.length) {
            return 0;
        }
        const giftCardAmount = this.computeGiftCardAmount();
        const loyaltyAmount = this.computedLoyaltyAmount();

        return (cart.TotalAmount || 0) - giftCardAmount - loyaltyAmount;
    }

    /**
     * Construct input object for AfterPay Create Order API
     * Builds object from checkout state/cart fields
     */
    @computed get getAfterpayOrderInput(): AfterpayCreateOrderDataActionInput | null {
        const data = this.props.data;
        const cart = data.checkout.result?.checkoutCart.cart;
        const checkoutState = data.checkout.result;

        if (!cart || !cart.CartLines || !cart.CartLines.length || !cart.ExtensionProperties) {
            this.props.telemetry.error('Missing cart data');
            return null;
        }

        if (!checkoutState) {
            this.props.telemetry.error('Missing checkout state');
            return null;
        }

        if (!this.isAmountDue) {
            this.props.telemetry.error('No amount due');
            return null;
        }

        // const firstName = cart.ExtensionProperties.find(prop => prop.Key === 'FIRSTNAME')?.Value?.StringValue;
        const firstName = checkoutState.shippingAddress?.Name;

        // const lastName = cart.ExtensionProperties.find(prop => prop.Key === 'LASTNAME')?.Value?.StringValue;
        const lastName = checkoutState.shippingAddress?.Name;
        // const phoneNumber = cart.ExtensionProperties.find(prop => prop.Key === 'PHONENUMBER')?.Value?.StringValue;
        const phoneNumber = checkoutState.shippingAddress?.Phone

        // if (!(firstName && lastName && phoneNumber)) {
        if (!(firstName && lastName)) {
            this.props.telemetry.error('missing contact details');
            return null;
        }

        // NOTE: this needs to match Afterpay account currency - NZD for NZ etc.
        const orderCurrency = this.getCurrency;

        // Address objects
        const billingObject = createAddressObject(checkoutState.billingAddress);
        const shippingObject = createAddressObject(checkoutState.shippingAddress);

        // Items array object
        const itemsObject = createOrderItemsObject(cart.CartLines, orderCurrency, this.props.data.products.result);
        const discountsObject = createDiscountsObject(cart.CartLines, checkoutState.giftCards, orderCurrency);
        const returnUrl = this.getCittaPaymentReturnUrl('AP');

        // Assemble createOrder input object
        const orderInput: IAfterpayCreateOrderInput = {
            totalAmount: { // REQUIRED
                amount: this.getAmountDue.toFixed(2), // REQUIRED
                currency: orderCurrency // REQUIRED
            },
            consumer: { // REQUIRED
                email: checkoutState.guestCheckoutEmail, // REQUIRED
                givenNames: firstName, // REQUIRED
                phoneNumber: phoneNumber,
                surname: lastName // REQUIRED
            },
            billing: billingObject,
            shipping: shippingObject,
            items: itemsObject,
            discounts: discountsObject,
            merchant: { // REQUIRED
                redirectCancelUrl: returnUrl, // REQUIRED
                redirectConfirmUrl: returnUrl // REQUIRED
            },
            merchantReference: cart.OrderNumber, // REQUIRED
            taxAmount: cart.TaxAmount ? {
                amount: cart.TaxAmount.toFixed(2),
                currency: orderCurrency
            } : undefined,
            shippingAmount: cart.ShippingChargeAmount ? {
                amount: cart.ShippingChargeAmount.toFixed(2),
                currency: orderCurrency
            } : undefined
        };

        this.logMessage(`Completed getAfterpayOrderInput - returnURL: ${returnUrl} amountDue: ${orderInput.totalAmount.amount} taxAmount: ${orderInput.taxAmount?.amount}
            routes: ${JSON.stringify(this.props.context.actionContext.requestContext.app.routes)}
            sitePath: ${this.props.context.actionContext.requestContext.sitePath}
            page url: ${window.location.href}
            location.pathname: ${window.location.pathname}
        `);
        return new AfterpayCreateOrderDataActionInput(orderInput);
    }

    // payment return URL for laybuy state
    // should match non-computed function
    @computed get getCittaLaybuyPaymentReturnUrl(): string {
        const baseUrl = window.location.origin;
        if (!baseUrl.length) {
            return '';
        }

        const checkoutPath = getUrlSync('checkout', this.props.context.actionContext) || '';
        const separator = checkoutPath.includes('?') ? '&' : '?';
        const url = decodeURI(window.location.search);
        const urlParams = new URLSearchParams(url);
        const cartId = urlParams.has('cartid') ? urlParams.get('cartid') : this.props.data.checkout.result?.checkoutCart?.cart?.Id;

        if (process.env.NODE_ENV !== 'production') {
            if (urlParams.has('mock')) {
                return `${baseUrl}/page?mock=${urlParams.get('mock')}&paymentMethod=LB&cartid=${cartId}`;
            }
        }

        return `${baseUrl}${checkoutPath}${separator}paymentMethod=LB&cartid=${cartId}`;
    }

    /**
     * Returns status of checkout data action
     */
    @computed get checkoutStatus(): string {
        return get(this.props, 'data.checkout.status');
    }

    /**
     * checks if the cart exists and is not empty
     */
    @computed get isEmptyCart(): boolean {
        if (this.isEditor) {
            // Editorial mode: Ignore empty cart
            return false;
        }
        const checkout = this.props.data.checkout.result;
        const cart = get(checkout, 'checkoutCart.cart');
        return this.checkoutStatus === 'SUCCESS' && !!cart && !isEmpty(cart) && (!cart.CartLines || cart.CartLines.length === 0);
    }

    /**
     * checks if this is a live environment or the authoring tool editor
     */
    @computed get isEditor(): boolean {
        return get(this.props, 'context.request.params.isEditor');
    }

    public state: ICheckoutPaymentInstrumentState = {
        ccNumber: '',
        ccName: '',
        ccExpiryMonth: '01',
        ccExpiryYear: new Date().getFullYear(),
        ccCvc: '',
        ccSessionId: '',
        cardNameError: false,
        cardNumError: false,
        cardCvcError: false,
        cardExpiryError: false,

        afterpayAvailable: false,
        afterpayConfiguration: null,
        afterpayMin: 0,
        afterpayMax: 0,
        afterpayOrderToken: '',

        laybuyAvailable: false,

        isBusy: false,
        isProcessingOrder: false
    };

    private cittaTelemetry: CittaTelemetry = new CittaTelemetry(this.props.context.app.config.cittaInstrumentationKey);

    @observable private hasRedirect: boolean = false;
    @observable private laybuyOrderToken: string = '';
    @observable private afterpayOrderToken: string = '';
    private ourGiftToYou: boolean = false;

    /**
     * Laybuy state object
     */
    @observable private laybuyState: ILaybuyButtonData = {
        isError: false,
        data: {
            checkout: this.props.data.checkout,
            products: this.props.data.products
        },
        currency: this.getCurrency,
        returnURL: this.getCittaLaybuyPaymentReturnUrl,
        actionContext: this.props.context.actionContext,
        context: this.props.context
    };

    public componentDidMount(): void {
        // @ts-ignore: Compiler not reconizing condition check for function params
        when(
            () => this.asyncResultStatus !== 'LOADING',
            () => {
                this.init();
            }
        );

        this.ourGiftToYou = this.props.config.ourGiftToYou || false;
    }

    // tslint:disable-next-line:cyclomatic-complexity
    public render(): JSX.Element | null {

        const {
            visibilityObserver
        } = this.props;

        const isVisible = visibilityObserver && visibilityObserver.isVisible;

        const ExtensionProperties = this.props.data.checkout?.result?.checkoutCart.cart?.ExtensionProperties;
        const _isLaybuyAvailable = stringToBoolean(ExtensionProperties?.find(prop => prop.Key === 'DXC_ISLAYBUYAVAILABLE')?.Value!.StringValue!);
        const _isAfterpayAvailable = stringToBoolean(ExtensionProperties?.find(prop => prop.Key === 'DXC_ISAFTERPAYAVAILABLE')?.Value!.StringValue!);
        const _isPaymentExpressAvailable = stringToBoolean(ExtensionProperties?.find(prop => prop.Key === 'DXC_ISPAYMENTEXPRESSAVAILABLE')?.Value!.StringValue!);

        const viewProps: ICheckoutPaymentInstrumentViewProps = {
            ...this.props,
            ...this.state,
            isVisible,

            // Citta payment params
            paymentMethod: paymentStore.paymentMethod,
            paymentTabs: this.renderPaymentInstrumentTabs(_isLaybuyAvailable, _isAfterpayAvailable, _isPaymentExpressAvailable),
            paymentTabContent: this.renderPaymentTabContent(_isLaybuyAvailable, _isAfterpayAvailable, _isPaymentExpressAvailable),
            processingModal: this.renderOrderProcessingModal()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Initial setup method for module
     *
     * loads needed scripts, then processes url for successful order on redirection
     */
    private setupModule = async (): Promise<void> => {
        // check for gift cards in cart
        const cart = this.props.data.checkout?.result?.checkoutCart.cart;

        if (cart) {
            // Add afterpay modal script to page html
            const afterpayScript = document.createElement('script');
            afterpayScript.src = cart.ExtensionProperties!.find(prop => prop.Key === 'DXC_AFTERPAYSCRIPTURL')!.Value!.StringValue!;
            afterpayScript.async = true;
            document.body.appendChild(afterpayScript);
        }

        if (cart && cart.CartLines && cart.CartLines.length > 0) {
            const giftCard = cart.CartLines.find((item) => item.ItemId === 'GiftCard');
            if (!giftCard) {
                // only setup afterpay if no gift cards in cart
                // get Afterpay configuration
                const afterpayConfig: IDXC_GetConfigurationResponse = await this.afterpayGetConfiguration().catch(error => {
                    this.logError(error, 'afterpayGetConfiguration failed');
                    this.displayCheckoutError(this.props.config.apNotAvailable);
                    this.setState({
                        afterpayAvailable: false
                    });
                    throw error;
                });
                this.processAfterpayConfig(afterpayConfig);

                this.setState({
                    laybuyAvailable: true
                });
            }
        }

        // parse url for order success
        // called after getConfig function to avoid potential conflicts
        await this.parseUrlParameters();
    }

    /**
     * Parse url parameters for redirection handling
     */
    private parseUrlParameters = async (): Promise<void> => {

        //add some logging here
        const url: string = decodeURI(window.location.search);
        const urlParams: URLSearchParams = new URLSearchParams(url);
        this.hasRedirect = urlParams.has('paymentMethod') && urlParams.has('cartid');
        this.logMessage(`landed on the checkout page with the URL: ${url}`);
        when(
            () => {
                return this.checkoutStatus === 'SUCCESS' && this.hasRedirect;
            },
            async () => {
                this.redirectLog(url);
                try {
                    this.logMessage(`Starting _handleReturnActions ${process.env.npm_package_version}`);
                    await this.handleReturnActions(urlParams);
                } catch (error) {
                    //@ts-ignore
                    this.logError(error, `componentDidMount hasRedirect failed: ${JSON.stringify(error)}`);
                }
            }
        );

        // @ts-ignore: Compiler not reconizing condition check for function params
        when(
            () => {
                return this.isEmptyCart && !this.hasRedirect;
            },
            () => {
                this.redirectOnError();
            }
        );
    }

    private async _loadCartData(extensionProps: CommerceProperty[]): Promise<void> {
        const data = this.props.data;

        if (data.checkout.result && extensionProps && extensionProps.length) {
            this.logMessage(`Starting _loadCartData ${getExtensionPropsLength(extensionProps)}`);
            const checkoutState = data.checkout.result;

            const GC = extensionProps.filter((item: CommerceProperty) => {
                const key: string = item.Key!;
                return key.includes('GiftCard');
            });

            const GCTotal: number = GC.length / 4;
            for(let i = 0; i < GCTotal; i++) {

                const giftCardId = extensionProps.find((prop) => prop.Key === `GiftCardId_${i}`)?.Value?.StringValue;
                const giftCardPin = extensionProps.find((prop) => prop.Key === `GiftCardPin_${i}`)?.Value?.StringValue;
                try {
                    this.logMessage(`Starting getGiftCard i: ${i} - Id: ${maskGiftCard(giftCardId!)}`);
                    const giftCard = await this.getGiftCard(giftCardId!, giftCardPin!);
                    this.logMessage(`Finished getGiftCard - Id: ${maskGiftCard(giftCard?.Id!)} Balance: ${giftCard?.Balance}`);
                    if (giftCard) {
                        const ExtensionProperties: CommerceProperty[] = [];
                        ExtensionProperties.push({
                            Key: 'GIFTCARDPIN',
                            Value: {
                                StringValue: giftCardPin
                            }
                        });

                        giftCard.ExtensionProperties = ExtensionProperties;
                        checkoutState.addGiftCard({giftCard: giftCard});
                        this.logMessage(`checkoutState ${JSON.stringify(checkoutState.giftCards)}`);
                    } else {
                        this.logMessage(`giftCard object not found - Id: ${maskGiftCard(giftCardId!)}`);
                    }
                } catch(error) {
                    this.logMessage(`Error adding giftCard ${JSON.stringify(error)}`);
                }
            }
        } else {
            throw new Error('Cart object missing data');
        }
    }

    private getGiftCard = async (giftCardNumber: string, giftCardPinNumber: string): Promise<GiftCard | undefined> => {
        return getGiftCardBalanceCheck(new GiftCardBalanceCheckInput(giftCardNumber, giftCardPinNumber), this.props.context.actionContext)
            .then(activeGiftCard => {
                if (activeGiftCard instanceof Error) {
                    throw activeGiftCard;
                }
                if (!activeGiftCard.balance || activeGiftCard.balance === 0) {
                    throw new Error('Gift card has no balance left');
                }
                const cittaGiftCard = activeGiftCard;
                // tslint:disable-next-line: no-unnecessary-local-variable
                const giftCard: GiftCard = {
                    Id: giftCardNumber,
                    Balance: cittaGiftCard && cittaGiftCard.balance,
                    BalanceCurrencyCode: cittaGiftCard && cittaGiftCard.currencyCode
                };
                return giftCard;
            })
            .catch(() => {
                throw new Error('Invalid gift card code entered');
            });
    };

    /**
     * Processes URL paramaters to handle redirection from payment methods
     */
    private handleReturnActions = async (urlParams: URLSearchParams): Promise<void> => {
        this.setState({
            isProcessingOrder: true
        });
        paymentStore.paymentMethod = urlParams.get('paymentMethod')!;
        const { OrderNumber } = this.props.data?.checkout?.result!.checkoutCart.cart;
        this.logMessage(`Get order number from cart: ${OrderNumber}`);

        // const cartId = urlParams.get('cartid')!;

        // TODO: Rework this check - or remove cartId from Url?
        /**
         * This check originally existed to confirm that we had reconstructed the correct cart object on return from redirection.
         * It shouldn't be needed with SSK2.0, but I'll leave the code here as a marker if a similar check needs to be added in future.
         */
        // console.log(`${cartId} -- ${Id}`);
        // if (cartId !== Id) {
        //     this.logMessage(`cartId mismatch from url & object - cartId: ${cartId} cart.Id: ${Id}`);
        //     this.setState({
        //         isProcessingOrder: false
        //     });
        //     throw new Error('cartId mismatch from url & object');
        // }

        try {
            const cart = this.props.data.checkout?.result?.checkoutCart.cart;
            if (cart?.ExtensionProperties) {
                await this._loadCartData(cart?.ExtensionProperties!);
            }
        } catch (error) {
            this.logMessage(`_loadCartData failed: ${JSON.stringify(error)}`);
            this.redirectOnError();
            this.logMessage(`completed redirectOnError`);
        }

        // switch according to payment method
        switch (paymentStore.paymentMethod) {
            case 'CC': {
                await this.checkCreditCardPayment(urlParams, OrderNumber!);
                break;
            }
            case 'AP': {
                await this.checkAfterpayPayment(urlParams);
                break;
            }
            case 'LB': {
                await this.checkLaybuyPayment(urlParams);
                break;
            }
            default: {
                this.logMessage(`Invalid payment method: ${paymentStore.paymentMethod}`);
                this.setState({
                    isProcessingOrder: false
                });
            }
        }
    }

    /**
     * Checks that credit card payment is valid and proceeds with place order process
     */
    private checkCreditCardPayment = async (urlParams: URLSearchParams, orderID: string): Promise<void> => {
        // check for credit card success
        if (urlParams.has('sessionid')) {
            const sessionId = urlParams.get('sessionid') || '';
            const actionInput = new paymentExpressGetTransactionInput(sessionId);
            this.logMessage(`Action input from sessionId: ${sessionId}, ${actionInput.transactionId}`);
            this.logMessage('Starting getCCTransaction');
            const retryMax = 3;
            const retryDelay = 10000;
            let retryCount = 0;
            let stopLoop = false;
            // tslint:disable-next-line
            const delay = (ms: number) => new Promise(res => setTimeout(res, ms));
            // This loop retries on specific status codes that indicate the order may not be ready yet.
            while (!stopLoop) {
                stopLoop = true;
                await this.getCCTransaction(actionInput).then(async (GetTransactionEntityExtensionClass) => {
                    paymentStore.paymentMethod = PaymentMethods.CreditCard;
                    this.logMessage(`getCCTransaction API call successfully
                        Amount: ${GetTransactionEntityExtensionClass.Amount}
                        TxnData1: ${GetTransactionEntityExtensionClass.TxnData1}
                        Status: ${GetTransactionEntityExtensionClass.Status}
                        ResponseCode: ${GetTransactionEntityExtensionClass.ResponseCode}
                        ResponseText: ${GetTransactionEntityExtensionClass.ResponseText}
                        orderID: ${orderID}
                        DPSTxnRef: ${GetTransactionEntityExtensionClass.DpstxnRef}
                        SessionId: ${GetTransactionEntityExtensionClass.SessionId}
                    `);
                    paymentStore.paymentMethod = PaymentMethods.CreditCard;
                    switch (GetTransactionEntityExtensionClass.Status) {
                        case '0': {
                            this.logMessage(`Check payment order IDs match - TxnData1: ${GetTransactionEntityExtensionClass.TxnData1}, orderID ${orderID}, Status: ${GetTransactionEntityExtensionClass.ResponseCode}`);
                            if (GetTransactionEntityExtensionClass.TxnData1 === orderID) {
                                const checkoutDetails = this.createCheckoutDetailsObject(
                                    PaymentMethods.CreditCard,
                                    GetTransactionEntityExtensionClass.Id,
                                    GetTransactionEntityExtensionClass.CardToken,
                                    GetTransactionEntityExtensionClass.ServiceAccountId,
                                    GetTransactionEntityExtensionClass.UniqueCardId,
                                    GetTransactionEntityExtensionClass.CardNumber,
                                    GetTransactionEntityExtensionClass.CardName,
                                    GetTransactionEntityExtensionClass.DateExpiry,
                                    GetTransactionEntityExtensionClass.TxnRef,
                                    GetTransactionEntityExtensionClass.DpstxnRef
                                );
                                await this.processCheckout(PaymentMethods.CreditCard, checkoutDetails);
                            } else {
                                const message = this.props.config.ccPaymentFailed || CC_PAYMENT_FAILED_DEFAULT;
                                this.logMessage(`Credit card payment rejected: ${GetTransactionEntityExtensionClass.ResponseText} - ${message}`);

                                if (GetTransactionEntityExtensionClass.ResponseCode === '00') {
                                    this.logMessage(`ALERT - payment order Ids do not match. OrderId: ${orderID}, TxnData1: ${GetTransactionEntityExtensionClass.TxnData1}, TxnRef: ${GetTransactionEntityExtensionClass.TxnRef}`);
                                    const checkoutDetails = this.createCheckoutDetailsObject(
                                        PaymentMethods.CreditCard,
                                        GetTransactionEntityExtensionClass.Id,
                                        GetTransactionEntityExtensionClass.CardToken,
                                        GetTransactionEntityExtensionClass.ServiceAccountId,
                                        GetTransactionEntityExtensionClass.UniqueCardId,
                                        GetTransactionEntityExtensionClass.CardNumber,
                                        GetTransactionEntityExtensionClass.CardName,
                                        GetTransactionEntityExtensionClass.DateExpiry,
                                        GetTransactionEntityExtensionClass.TxnRef,
                                        GetTransactionEntityExtensionClass.DpstxnRef
                                    );
                                    await this.processCheckout(PaymentMethods.CreditCard, checkoutDetails);

                                }
                                else {
                                    this.displayCheckoutError(`${GetTransactionEntityExtensionClass.ResponseText}: ${message}`, PaymentMethods.CreditCard);
                                }
                            }
                            break;
                        }
                        case '2': {
                            retryCount++;
                            if (retryCount <= retryMax) {
                                stopLoop = false;
                                await delay(retryDelay);
                            } else {
                                const message = this.props.config.ccPaymentFailed || CC_PAYMENT_FAILED_DEFAULT;
                                this.logMessage(`Credit card payment failed after retry - status 2: ${GetTransactionEntityExtensionClass.ResponseText} - ${message}`);
                                this.displayCheckoutError(`${GetTransactionEntityExtensionClass.ResponseText}: ${message}`, PaymentMethods.CreditCard);
                            }
                            break;
                        }
                        case '4': {
                            retryCount++;
                            if (retryCount <= retryMax) {
                                stopLoop = false;
                                await delay(retryDelay);
                            } else {
                                const message = this.props.config.ccPaymentFailed || CC_PAYMENT_FAILED_DEFAULT;
                                this.logMessage(`Credit card payment failed after retry - status 4: ${GetTransactionEntityExtensionClass.ResponseText} - ${message}`);
                                this.displayCheckoutError(`${GetTransactionEntityExtensionClass.ResponseText}: ${message}`, PaymentMethods.CreditCard);
                            }
                            break;
                        }
                        default: {
                            const message = this.props.config.ccPaymentFailed || CC_PAYMENT_FAILED_DEFAULT;
                            this.logMessage(`Credit card payment rejected: ${GetTransactionEntityExtensionClass.ResponseText} - ${message}`);
                            this.displayCheckoutError(`${GetTransactionEntityExtensionClass.ResponseText}: ${message}`, PaymentMethods.CreditCard);
                        }
                    }
                }).catch((error) => {
                    const message = this.props.config.ccPaymentFailed || CC_PAYMENT_FAILED_DEFAULT;
                    this.logError(error, `_getCCTransaction CC payment failed: ${JSON.stringify(error)} - ${message}`);
                    this.displayCheckoutError(message, PaymentMethods.CreditCard);
                    throw error;
                });
            }
        } else {
            this.logMessage('Credit card missing sessionid in url parameters');
        }
    }

    /**
     * Checks afterpay order is valid and proceeds to place order
     */
    private checkAfterpayPayment = async (urlParams: URLSearchParams): Promise<void> => {
        paymentStore.paymentMethod = PaymentMethods.Afterpay;
        // check for afterpay success
        if (urlParams.get('paymentMethod') === 'AP' && urlParams.has('orderToken')) {
            const orderToken = urlParams.get('orderToken');
            const afterpayStatus = urlParams.get('status') || 'FAILURE';

            if (afterpayStatus === 'SUCCESS' && orderToken) {
                // appears successful - check with getOrder API
                this.logMessage(`Starting _afterpayGetOrder API call`);
                await this.afterpayGetOrder(orderToken).then(async (getOrderResponse) => {
                    this.logMessage(`afterpayGetOrder API called successfully
                        errorText: ${getOrderResponse.errorText || 'N/A'}
                        expires: ${getOrderResponse.order?.expires || 'N/A'}
                        merchantReference: ${getOrderResponse.order?.merchantReference || 'N/A'}
                        totalAmount: ${getOrderResponse.order?.totalAmount?.amount || 'N/A'}
                        success: ${getOrderResponse.success}
                    `);
                    if (getOrderResponse.success && getOrderResponse.order) {
                        this.afterpayOrderToken = orderToken;
                        this.logMessage('Starting _processCheckout');
                        await this.processCheckout(PaymentMethods.Afterpay);
                    } else {
                        // ERROR STATE
                        const message = this.props.config.apServiceError || AP_SERVICE_ERROR_DEFAULT;
                        this.logMessage(`afterpayGetOrder API results failed check - ${message}`);
                        this.displayCheckoutError(message, PaymentMethods.Afterpay);
                    }
                }).catch(error => {
                    const message = this.props.config.apPaymentFailed || AP_PAYMENT_FAILED_DEFAULT;
                    this.logError(error, `afterpayGetOrder API call failed: ${JSON.stringify(error)} - ${message}`);
                    this.displayCheckoutError(message, PaymentMethods.Afterpay);
                    throw error;
                });
            } else {
                const message = this.props.config.apPaymentFailed || AP_PAYMENT_FAILED_DEFAULT;
                this.logMessage(`Afterpay status parms not SUCCESS: ${afterpayStatus} - ${message}`);
                this.displayCheckoutError(message, PaymentMethods.Afterpay);
            }
        } else {
            this.logMessage('Afterpay missing url parameters');
            this.displayCheckoutError('Afterpay missing url parameters', PaymentMethods.Afterpay);
        }
    }

    /**
     * Checks laybuy order is valid & proceeds to place order
     */
    private checkLaybuyPayment = async (urlParams: URLSearchParams): Promise<void> => {
        this.logMessage(`Starting checkLaybuyPayment`);
        if (urlParams.get('paymentMethod') === 'LB' && urlParams.has('token')) {
            paymentStore.paymentMethod = PaymentMethods.Laybuy;
            const token = urlParams.get('token') || '';
            const status = urlParams.get('status') || 'FAILURE';

            if (status === 'SUCCESS') {
                this.laybuyOrderToken = token;
                try {
                    this.logMessage(`Starting _processCheckout - success ${token}`);
                    await this.processCheckout(PaymentMethods.Laybuy);
                } catch (error) {
                    //@ts-ignore
                    this.logError(error, `processCheckout failed: ${JSON.stringify(error)}`);
                    throw error;
                }
            } else if (status === 'CANCELLED') {
                this.displayCheckoutError('Laybuy order cancelled', PaymentMethods.Laybuy);
            }
        } else {
            this.logMessage('Laybuy missing url parameters');
            this.displayCheckoutError('Laybuy order failed, please try again', PaymentMethods.Laybuy);
        }
    }

    /**
     * Calls payment finalization APIs and then checkout function
     * @param paymentMethod string to identify payment method
     */
    // tslint:disable-next-line
    private processCheckout = async (paymentMethod: PaymentMethods, creditCardCheckoutDetails?: ICheckoutDetailsObject) => {
        // CHECK DATA/CALL CHECKOUT
        switch (paymentMethod) {
            case PaymentMethods.CreditCard: {
                try {
                    this.logMessage(`Starting CC checkout`);
                    // Proceed to checkout
                    await this.checkout(creditCardCheckoutDetails);
                } catch (error) {
                    //@ts-ignore
                    this.logError(error, `checkout failed ${JSON.stringify(error)}`);
                    this.displayCheckoutError(`error: ${error}`, PaymentMethods.CreditCard);
                }
                break;
            }
            case PaymentMethods.Afterpay: {
                try {
                    this.logMessage('Starting _afterpayCapturePayment');
                    const capturePaymentResponse = await this.afterpayCapturePayment();
                    this.logMessage(`_afterpayCapturePayment API call successfully
                        success: ${capturePaymentResponse.success}
                        created: ${capturePaymentResponse.payment?.created || 'N/A'}
                        merchantReference: ${capturePaymentResponse.payment?.merchantReference || 'N/A'}
                        status: ${capturePaymentResponse.payment?.status || 'N/A'}
                        totalAmount: ${capturePaymentResponse.payment?.totalAmount?.amount || 'N/A'}
                        currency: ${capturePaymentResponse.payment?.totalAmount?.currency || 'N/A'}
                        errorText: ${capturePaymentResponse.errorText || 'N/A'}
                    `);
                    if (capturePaymentResponse.success && capturePaymentResponse.payment && capturePaymentResponse.payment.status === 'APPROVED') {
                        const checkoutDetails = this.createCheckoutDetailsObject(
                            PaymentMethods.Afterpay,
                            capturePaymentResponse.payment.id,
                            capturePaymentResponse.cardToken,
                            capturePaymentResponse.serviceAccountId,
                            capturePaymentResponse.uniqueCardId
                        );
                        try {
                            this.logMessage(`Starting AP checkout`);
                            // Proceed to checkout
                            await this.checkout(checkoutDetails);
                        } catch (error) {
                            //@ts-ignore
                            this.logError(error, `Process checkout - AP error`);
                            this.displayCheckoutError(`error: ${error}`, PaymentMethods.Afterpay);
                        }
                    } else {
                        const message = this.props.config.apPaymentError || AP_PAYMENT_ERROR_DEFAULT;
                        this.logMessage(`Process checkout - AP error 2 - ${message}`);
                        this.displayCheckoutError(message, PaymentMethods.Afterpay);
                    }
                } catch (error) {
                    //@ts-ignore
                    this.logError(error, `_afterpayCapturePayment failed ${JSON.stringify(error)}`);
                }
                break;
            }
            case PaymentMethods.Laybuy: {
                try {
                    this.logMessage('Starting confirmLaybuyOrder');
                    const response = await confirmLaybuyOrder(
                        this.laybuyOrderToken,
                        this.props.context.actionContext
                    );
                    this.logMessage(`confirmLaybuyOrder API call successfully
                        result: ${response.result}
                        orderId: ${response.orderid}
                    `);
                    // Laybuy orderId is different to the merchantRef/orderID, so no check is done
                    if (response.result === 'SUCCESS') {
                        const checkoutDetails = this.createCheckoutDetailsObject(
                            PaymentMethods.Laybuy,
                            response.orderid,
                            response.cardToken,
                            response.serviceAccountId,
                            response.uniqueCardId,
                            response.CardNumber
                        );
                        try {
                            this.logMessage(`Starting LB checkout`);
                            // Proceed to checkout
                            await this.checkout(checkoutDetails);
                        } catch (error) {
                            //@ts-ignore
                            this.logError(error, `Processing checkout failed - LB`);
                            throw error;
                        }
                    } else if (response.result === 'DECLINED') {
                        this.logMessage('Laybuy payment declined, laybuyCancelOrderAsync API starting');
                        await laybuyCancelOrderAsync({ callerContext: this.props.context.actionContext }, this.laybuyOrderToken).catch(error => {
                            this.logError(error, 'laybuyCancelOrderAsync failed');
                            throw error;
                        });
                        this.logMessage('laybuyCancelOrderAsync API called successfully');
                        this.displayCheckoutError(response.error, PaymentMethods.Laybuy);
                    } else {
                        this.logMessage('Laybuy payment not accepted');
                        this.displayCheckoutError(response.error, PaymentMethods.Laybuy);
                    }
                } catch (error) {
                    //@ts-ignore
                    this.logError(error, `confirmLaybuyOrder API failed - Process checkout error: ${JSON.stringify(error)}`);
                     //@ts-ignore
                    this.displayCheckoutError(error, PaymentMethods.Laybuy);
                    throw error;
                }
                break;
            }
            default: {
                this.logMessage(`${paymentStore.paymentMethod} : invalid payment method`);
                this.setState({
                    isProcessingOrder: false
                });
            }
        }
    }

    /**
     * get transaction result from retail proxy file
     * return Promise Object
     */
    private getCCTransaction = async (actionInput: paymentExpressGetTransactionInput): Promise<IGetTransactionEntity> => {
        this.logMessage(`getCCTransaction Passing ${actionInput.transactionId}`);
        return getTransactionAction(actionInput, this.props.context.actionContext);
    }

    /**
     * Creates object with payment details to pass to place order function
     */
    private createCheckoutDetailsObject = (
        paymentMethod: string,
        orderId?: string,
        cardToken?: string,
        serviceAccountId?: string,
        uniqueCardId?: string,
        maskedCardNumber?: string,
        cardName?: string,
        cardDateExpiry?: string,
        TxnRef?: string,
        DpstxnRef?: string
    ): ICheckoutDetailsObject => {
        return {
            paymentMethod: paymentMethod,
            orderId: orderId,
            cardToken: cardToken,
            serviceAccountId: serviceAccountId,
            uniqueCardId: uniqueCardId,
            maskedCardNumber: maskedCardNumber,
            cardName: cardName,
            cardDateExpiry: cardDateExpiry,
            TxnRef: TxnRef,
            DpstxnRef: DpstxnRef
        };
    }

    /**
     * Logs page URL on redirection
     */
    private redirectLog = (url: string) => {
        const urlParams: URLSearchParams = new URLSearchParams(url);
        urlParams.delete('token'); // laybuy
        urlParams.delete('orderToken'); // afterpay
        urlParams.delete('sessionid'); // payment express

        this.logMessage(`Starting ejected checkout module: ${window.location.pathname}?${urlParams.toString()}`);
    }

    /**
     * Redirects to cart page
     */
    private redirectOnError = () => {
        const editCartLink = getUrlSync('cart', this.props.context.actionContext) || '';
        if (window && editCartLink) {
            this.logMessage('Redirecting');
            // Redirect to cart page if cart is empty
            window.location.assign(editCartLink);
        } else {
            this.logMessage('Cart is empty, but no editCartLink found');
        }
    }

    /**
     * Saves Afterpay config to state and determines if Afterpay payment option is available
     */
    private processAfterpayConfig = (afterpayConfig: IDXC_GetConfigurationResponse): void => {
        // Set configuration value in state
        if (afterpayConfig && afterpayConfig.success && afterpayConfig.configuration) {
            const configResult: IConfiguration = afterpayConfig.configuration[0];
            // Parse result object for state - convert amounts to numbers
            // undefined or null converts to 0 NZD
            const configObject: IAfterpayConfigObject = {
                type: configResult.type,
                minimumAmount: configResult.minimumAmount ? {
                    amount: configResult.minimumAmount.amount && configResult.minimumAmount.amount !== '' ?
                        +configResult.minimumAmount.amount : 0,
                    currency: this.getCurrency
                } : {
                    amount: 0,
                    currency: 'NZD'
                },
                maximumAmount: configResult.maximumAmount ? {
                    amount: configResult.maximumAmount.amount && configResult.maximumAmount.amount !== '' ?
                        +configResult.maximumAmount.amount : 0,
                    currency: this.getCurrency
                } : {
                    amount: 0,
                    currency: 'NZD'
                }
            };
            // store in state
            this.setState({
                afterpayConfiguration: configObject,
                afterpayMin: configObject.minimumAmount.amount,
                afterpayMax: configObject.maximumAmount.amount
            });

            this.checkAfterpayAvailability();
        } else {
            this.displayCheckoutError(this.props.config.apNotAvailable, PaymentMethods.Afterpay);
            this.setState({
                afterpayAvailable: false
            });
            this.props.telemetry.error('Afterpay getConfig error in setup');
        }
    }

    /**
     * Checks if Afterpay is available for the current order total
     */
    private checkAfterpayAvailability = (): void => {
        if (!this.state.afterpayConfiguration) {
            if (this.state.afterpayAvailable) {
                this.setState({ afterpayAvailable: false });
            }
            return;
        }

        const total = this.getAmountDue;
        const min = this.state.afterpayMin;
        const max = this.state.afterpayMax;
        const isValidAmount = min < total && total <= max;

        if (isValidAmount) {
            if (!this.state.afterpayAvailable) {
                this.setState({ afterpayAvailable: true });
            }
        } else {
            if (this.state.afterpayAvailable) {
                this.setState({ afterpayAvailable: false });
            }
        }
    }

    /**
     * Sets the initially active payment tab depending on available payment options
     */
    private setInitialActiveTab = (): void => {
        if (paymentStore.paymentMethod !== '') {
            // already set due to redirection
            // this should run before redirection code, but this check is here just in case
            return;
        }
        const ExtensionProperties = this.props.data.checkout?.result?.checkoutCart.cart?.ExtensionProperties;
        const _isLaybuyAvailable = stringToBoolean(ExtensionProperties?.find(prop => prop.Key === 'DXC_ISLAYBUYAVAILABLE')?.Value!.StringValue!);
        const _isAfterpayAvailable = stringToBoolean(ExtensionProperties?.find(prop => prop.Key === 'DXC_ISAFTERPAYAVAILABLE')?.Value!.StringValue!);
        const _isPaymentExpressAvailable = stringToBoolean(ExtensionProperties?.find(prop => prop.Key === 'DXC_ISPAYMENTEXPRESSAVAILABLE')?.Value!.StringValue!);

        let method: string = '';
        if (_isPaymentExpressAvailable) {
            method = PaymentMethods.CreditCard;
        } else if (_isAfterpayAvailable) {
            method = PaymentMethods.Afterpay;
        } else if (_isLaybuyAvailable) {
            method = PaymentMethods.Laybuy;
        }

        paymentStore.paymentMethod = method;
    }

    /**
     * Calls afterpay GetConfig Api and returns result
     */
    private afterpayGetConfiguration = async (): Promise<IDXC_GetConfigurationResponse> => {
        const actionInput = new AfterpayGetConfigDataActionInput();
        return IAfterpayGetConfigDataActionAction(actionInput, this.props.context.actionContext).catch(error => {
            this.logError(error, 'IAfterpayGetConfigDataActionAction failed');
            throw error;
        });
    }

    /**
     * Calls Afterpay GetOrder Api and returns result
     * @param token order token
     */
    private afterpayGetOrder = async (token: string): Promise<IDXC_GetOrderResponse> => {
        const actionInput = new AfterpayGetOrderDataActionInput(token);
        return IAfterpayGetOrderDataActionAction(actionInput, this.props.context.actionContext);
    }

    /**
     * Calls Afterpay CapturePayment Api and stores result if successful
     */
    private afterpayCapturePayment = async (): Promise<IDXC_CapturePaymentResponse> => {
        const actionInput = new AfterpayCapturePaymentDataActionInput(this.afterpayOrderToken);
        return IAfterpayCapturePaymentDataActionAction(actionInput, this.props.context.actionContext);
    }

    /**
     * Renders payment tabs depending on which services are available
     */
    private renderPaymentInstrumentTabs = (isLaybuyAvailable: boolean, isAfterpayAvailable: boolean, isPaymentExpressAvailable: boolean): React.ReactNode => {
        return (
            <div className='col citta-payment-options' role='tablist'>
                {isLaybuyAvailable &&
                    <CittaPaymentInstrumentTab
                        isDisabled={!this.isAmountDue || !this.state.laybuyAvailable}
                        isChecked={paymentStore.paymentMethod === PaymentMethods.Laybuy}
                        imgSource={this.props.config.laybuyImageUrl ? this.props.config.laybuyImageUrl : 'https://files-ap-prod.cms.commerce.dynamics.com/cms/api/jwkdjhjctq/binary/MB6e7'}
                        imgHeight='130px'
                        value='lb'
                        tabName='laybuy'
                        handleKeyDown={this.handleKeyDown}
                        handleTabClick={this.handleTabClick}
                    />
                }
                {isAfterpayAvailable &&
                    <CittaPaymentInstrumentTab
                        isDisabled={!this.isAmountDue || !this.state.afterpayAvailable}
                        isChecked={paymentStore.paymentMethod === PaymentMethods.Afterpay}
                        imgSource={this.props.config.afterpayImageUrl ? this.props.config.afterpayImageUrl : 'https://files-ap-prod.cms.commerce.dynamics.com/cms/api/tstpxgfmq/binary/MFfBP'}
                        imgWidth='100px'
                        value='ap'
                        tabName='afterpay'
                        handleKeyDown={this.handleKeyDown}
                        handleTabClick={this.handleTabClick}
                    />
                }
                {isPaymentExpressAvailable &&
                    <CittaPaymentInstrumentTab
                        isDisabled={!this.isAmountDue}
                        isChecked={paymentStore.paymentMethod === PaymentMethods.CreditCard}
                        labelString='Credit / Debit Card'
                        value='cc'
                        tabName='creditdebitcard'
                        handleKeyDown={this.handleKeyDown}
                        handleTabClick={this.handleTabClick}
                    />
                }
            </div>
        );
    }

    /**
     * Renders tab content for the currently selected payment method
     */
    private renderPaymentTabContent = (isLaybuyAvailable: boolean, isAfterpayAvailable: boolean, isPaymentExpressAvailable: boolean): React.ReactNode => {
        let tabContent: React.ReactNode;
        if (this.isAmountDue) {
            // render tab content depending on payment method
            switch (paymentStore.paymentMethod) {
                case PaymentMethods.CreditCard:
                    tabContent = isPaymentExpressAvailable && this.renderCreditCardTab();
                    break;
                case PaymentMethods.Afterpay:
                    tabContent = isAfterpayAvailable && this.renderAfterpayTab();
                    break;
                case PaymentMethods.Laybuy:
                    tabContent = isLaybuyAvailable && this.renderLaybuyTab();
                    break;
                default:
            }
        } else {
            tabContent = (
                <div className='no-amount-due-container'>
                    <strong>No further payment required. Thank you!</strong>
                    <p>Please place your order below.</p>
                    {this.renderPlaceOrderButton()}
                </div>
            );
        }
        return (
            <div className='col citta-payment-content'>
                {this.renderCheckoutError()}
                {tabContent}
            </div>
        );
    }

    /**
     * Renders credit card/payment express tab content
     */
    private renderCreditCardTab = (): React.ReactNode => {
        const checkout = this.props.data.checkout.result!;
        const { ExtensionProperties, Id, OrderNumber } = checkout.checkoutCart.cart;
        const { guestCheckoutEmail } = checkout;
        const paymentExpressPostForm = ExtensionProperties?.find(prop => prop.Key === 'DXC_PAYMENTEXPRESSPOSTURL')?.Value!.StringValue!;
        this.logMessage(`paymentExpressPostForm Extension Propertie ${paymentExpressPostForm}`);
        const markupData: ICCMarkupData = {
            Id: Id,
            OrderNumber: OrderNumber,
            guestCheckoutEmail: guestCheckoutEmail,
            postForm: paymentExpressPostForm,
            cvc: this.state.ccCvc,
            expiryMonth: this.state.ccExpiryMonth,
            expiryYear: this.state.ccExpiryYear,
            name: this.state.ccName,
            number: this.state.ccNumber,
            sessionId: this.state.ccSessionId
        };
        return (
            <CittaPaymentCreditCardTabContent
                isBusy={this.state.isBusy}
                cardNameError={this.state.cardNameError}
                cardNumError={this.state.cardNumError}
                cardCvcError={this.state.cardCvcError}
                cardExpiryError={this.state.cardExpiryError}
                ccMarkupData={markupData}
                handleCCFormSubmit={this.handleCCFormSubmit}
                handleCCInputChange={this.handleCCInputChange}
                amexImageUrl={this.props.config.amexImageUrl}
                visaImageUrl={this.props.config.visaImageUrl}
                mastercardImageUrl={this.props.config.mastercardImageUrl}
            />
        );
    }

    /**
     * Renders Afterpay tab content
     */
    private renderAfterpayTab = (): React.ReactNode => {
        const imgSrc = this.props.config.afterpayImageUrl ? this.props.config.afterpayImageUrl : 'https://files-ap-prod.cms.commerce.dynamics.com/cms/api/tstpxgfmq/binary/MFfBP';
        return (
            <CittaPaymentAfterpayTabContent
                afterpayAvailable={this.state.afterpayAvailable}
                isBusy={this.state.isBusy}
                instalmentAmount={this.getAfterpayInstalment}
                afterpayImgSrc={imgSrc}
                processAfterpayOrder={this.processAfterpayOrder}
            />
        );
    }

    /**
     * Renders Laybuy tab content
     */
    private renderLaybuyTab = (): React.ReactNode => {
        const imgSrc = this.props.config.laybuyImageUrl ? this.props.config.laybuyImageUrl : 'https://files-ap-prod.cms.commerce.dynamics.com/cms/api/jwkdjhjctq/binary/MB6e7';
        return (
            <CittaPaymentLaybuyTabContent
                laybuyImgSrc={imgSrc}
                instalmentAmount={this.getLaybuyInstalment}
                errorMsg={this.props.config.completeAllFields ? this.props.config.completeAllFields : 'Please complete all required fields before continuing'}
                laybuyTabState={this.laybuyState}
                // @ts-ignore
                onClick={this._saveCheckoutDetails.bind(this)}
            />
        );
    }

    /**
     * Renders errors on the payment module
     */
    private renderCheckoutError = (): React.ReactNode | null => {
        const displayCheckoutError: boolean = paymentStore.checkoutErrorMessage &&
            (!paymentStore.checkoutErrorPaymentMethod || paymentStore.paymentMethod === paymentStore.checkoutErrorPaymentMethod);

        const wrapperClass = `payment-error-container ${displayCheckoutError ? 'showError' : ''}`;

        return (
            <div className={wrapperClass}>
                <div className='payment-error-text'>
                    <p>{paymentStore.checkoutMessageText}</p>
                </div>
            </div>
        );
    }

    /**
     * Renders Place Order button for orders where no payment is needed (ie: gift card) or testing
     */
    private renderPlaceOrderButton = (): React.ReactNode => {
        const styleObj = {
            width: '100%'
        };
        const callToActionProps = {
            className: 'payment-place-order__btn-place-order checkout-place-order__btn-place-order btn btn-primary',
            title: this.state.isBusy ? 'Please wait ' : 'Place Order',
            onClick: () => this.checkout(),
            glyph: this.state.isBusy ? 'spinner' : '',
            disabled: this.state.isBusy
        };
        return (
            <div className='payment-place-order' style={styleObj}>
                <button {...callToActionProps}>
                    {this.state.isBusy ? 'Please wait ' : 'Place Order'}
                </button>
            </div>
        );
    }

    /**
     * Renders Order Processing Modal.
     * Should display after proceeding with payment and upon returning after redirection
     */
    private renderOrderProcessingModal = (): React.ReactNode => {
        return (
            <div className='order-processing-modal'>
                <Modal
                    autoFocus={true}
                    fade={false}
                    isOpen={this.state.isProcessingOrder}
                    horizontalPosition={'center'}
                    verticalPosition={'center'}
                    zIndex={1000}
                    applicationNode={'rsg-root'}
                    className={'order-processing-modal-body'}
                >
                    <ModalBody
                        className={'modal-body'}
                    >
                        <h3>Your order is being processed</h3>
                        <br />
                        <h5>Please wait </h5>
                    </ModalBody>
                </Modal>
            </div>
        );
    }

    /**
     * Gets the return URL for the given payment method
     *
     * payment method ID string passed as Parameter (eg: 'CC', 'AP', 'LB')
     */
    private getCittaPaymentReturnUrl = (paymentMethod: string): string => {
        const baseUrl = window.location.origin;
        if (!baseUrl.length) {
            return '';
        }

        const checkoutPath = getUrlSync('checkout', this.props.context.actionContext) || '';
        const separator = checkoutPath.includes('?') ? '&' : '?';
        const url = decodeURI(window.location.search);
        const urlParams = new URLSearchParams(url);
        const cartId = urlParams.has('cartid') ? urlParams.get('cartid') : this.props.data.checkout.result?.checkoutCart?.cart?.Id;

        if (process.env.NODE_ENV !== 'production') {
            if (urlParams.has('mock')) {
                return `${baseUrl}/page?mock=${urlParams.get('mock')}&paymentMethod=${paymentMethod}&cartid=${cartId}`;
            }
        }

        return `${baseUrl}${checkoutPath}${separator}paymentMethod=${paymentMethod}&cartid=${cartId}`;
    }

    /**
     * Creates Afterpay order and redirects to Afterpay payment portal
     *
     * Called when user clicks 'Proceed with AfterPay'
     */
    private processAfterpayOrder = async (): Promise<void> => {
        // Set module state to busy
        this.setState({ isBusy: true });

        this.logMessage('Start adding ExtensionProperties');
        // add the extension properties here?
        await this._saveCheckoutDetails().catch(error => {
            this.logMessage('saveCheckoutDetails failed');
            this.props.telemetry.error('There was an error saving your details, please try again later', PaymentMethods.CreditCard);
            this.setState({
              showSuccess: false,
              isBusy: false,
              isProcessingOrder: false
            });
            throw error;
          });

        const createOrderResult = await this.afterpayCreateOrder().catch(error => {
            this.logError(error, 'afterpayCreateOrder failed');
            this.displayCheckoutError(this.props.config.apPaymentError, PaymentMethods.Afterpay);
            this.setState({
                isBusy: false,
                isProcessingOrder: false
            });
            throw error;
        });

        // check if error with create order

        if (!createOrderResult || !createOrderResult.success || !createOrderResult.token) {
            this.logMessage('afterpayCreateOrder failure');
            this.displayCheckoutError(this.props.config.apPaymentError, PaymentMethods.Afterpay);
            this.setState({
                isBusy: false,
                isProcessingOrder: false
            });
            return;
        }

        // Initialise Afterpay script then redirect to portal
        // get region code
        const channelConfig = this.props.data.channelConfiguration.result;
        const regionIsoCode = channelConfig ? channelConfig.ChannelCountryRegionISOCode : 'NZ'; // default to NZ
        // @ts-ignore
        AfterPay.initialize({ countryCode: regionIsoCode });

        this.logMessage('redirect to afterpay portal');

        // @ts-ignore
        AfterPay.redirect({ token: createOrderResult.token });
    }
    /**
     * Calls Afterpay CreateOrder Api and returns result
     */
    private afterpayCreateOrder = async (): Promise<IDXC_CreateOrderResponse | void> => {
        const afterpayActionInput = this.getAfterpayOrderInput;

        // check if successful order input
        if (afterpayActionInput === null) {
            this.displayCheckoutError(this.props.config.completeAllFields, PaymentMethods.Afterpay);
            return this.setState({
                isBusy: false
            });
        }

        // store order input in state
        this.setState({ afterpayOrderDetails: afterpayActionInput });
        // call create order API
        const createOrderResult: IDXC_CreateOrderResponse = await IAfterpayCreateOrderDataActionAction(afterpayActionInput, this.props.context.actionContext).catch(error => {
            this.logError(error, 'IAfterpayCreateOrderDataActionAction failed');
            this.displayCheckoutError(this.props.config.apPaymentError, PaymentMethods.Afterpay);
            this.setState({
                isBusy: false,
                isProcessingOrder: false
            });
            throw error;
        });

        // check if error with create order
        if (!createOrderResult || !createOrderResult.success || !createOrderResult.token) {
            // error with create order
            this.displayCheckoutError(this.props.config.apServiceError, PaymentMethods.Afterpay);
            this.setState({
                isBusy: false
            });
            return;
        }

        // store token
        this.setState({ afterpayOrderToken: createOrderResult.token });

        return createOrderResult;
    }

    /**
     * handles input change on credit card form
     */
    // tslint:disable-next-line:no-any
    private handleCCInputChange = (e: any): void => {
        this.setState({ [e.target.name]: e.target.value });
        showPaymentError(false);
    }

    /**
     *
     * SAVE CHECKOUT DETAILS
     */
    private async _saveCheckoutDetails() {
        const cart = this.props.data.checkout.result!.checkoutCart.cart;
        const checkoutState = this.props.data.checkout.result;

        // gift card details
        if (cart && checkoutState) {
            if (checkoutState.giftCards.length > 0) {
                await this._addGiftcardExtensionProperties(cart, checkoutState.giftCards);
            }

            await this._saveCartExtensions(cart).catch(error => {
                this.props.telemetry.error('saveCartExtensions failed');
                throw error;
            });

            const {
                customerAccountNumber,
                isAuthenticated,
                token
            } = this.props.context.request.user;

            this.logMessage(`ExtensionProperties _saveCheckoutDetails success - isAuthenticated: ${isAuthenticated}, hasToken: ${token ? 'true' : 'false'}, customerAccountNumber: ${customerAccountNumber}`);
        } else {
            this.props.telemetry.error('Error saving checkout details');
        }
    }

    /**
     * Adds gift card/s to extension props
     * @param cart cart
     */
    private _addGiftcardExtensionProperties(cart: Cart, giftcards: readonly GiftCard[]) {
        // TODO: add each gift card
        for (let i = 0; i < giftcards.length; i++) {
            const cardId = giftcards[i].Id;
            // @ts-ignore
            const cardPin = giftcards[i].Pin;

            const cardBalance = giftcards[i].Balance;
            const cardCurrency = giftcards[i].CardCurrencyCode;

            if (cardId && cardPin) {
                this._addCartExtension(cart, `GiftCardId_${i}`, cardId);
                this._addCartExtension(cart, `GiftCardPin_${i}`, cardPin);

                if (cardBalance) {
                    this._addCartExtensionNumber(cart, `GiftCardBalance_${i}`, cardBalance);
                }

                if (cardCurrency) {
                    this._addCartExtension(cart, `GiftCardCurrency_${i}`, cardCurrency);
                }
            }
        }
    }

    private async _saveCartExtensions(cart: Cart) {
        if (!cart) {
          this.logMessage(`saveCartExtensions error: No cart object given`);
          this.props.telemetry.error('No cart object given');
          throw new Error(`saveCartExtensions error: No cart object given`);
        }
        const cartExtensionProperties = cart.ExtensionProperties;

        await updateAsync({ callerContext: this.props.context.actionContext }, { Id: cart.Id, ExtensionProperties: cartExtensionProperties }).then(newCart => {
          if (newCart) {
            this.logMessage(`updateAsync cartId: ${newCart.Id} ${getExtensionPropsLength(newCart.ExtensionProperties!)}`);
            this.props.context.actionContext.update(new GetCheckoutCartInput(this.props.context.request.apiSettings), newCart);
          }
        }).catch(error => {
          this.logMessage(`saveCartExtensions error: ${error.statusCode} ${error.name} ${error.message} ${error.data.CorrelationId}`);
          throw new Error(`saveCartExtensions ${error}`);
        });
      }

    private _addCartExtensionNumber(cart: Cart, key: string, value: number) {
        cart.ExtensionProperties = cart.ExtensionProperties || [];
        const property = cart.ExtensionProperties.find((prop) => prop.Key === key);
        if (property) {
            property.Value = { DecimalValue: value };
        } else {
            cart.ExtensionProperties.push({
                Key: key,
                Value: {
                    DecimalValue: value
                }
            });
        }
    }

    private _addCartExtension(cart: Cart, key: string, value: string) {
        cart.ExtensionProperties = cart.ExtensionProperties || [];
        const property = cart.ExtensionProperties.find((prop) => prop.Key === key);
        if (property) {
            property.Value = { StringValue: value };
        } else {
            cart.ExtensionProperties.push({
                Key: key,
                Value: {
                    StringValue: value
                }
            });
        }
    }

    /**
     * Handles Credit card form submit
     */
    // tslint:disable-next-line:prefer-method-signature
    private handleCCFormSubmit = async (e: { preventDefault: () => void }) => {
        this.setState({
            isBusy: true,
            isProcessingOrder: true
        });

        e.preventDefault();

        this.logMessage('Start adding ExtensionProperties');
        // add the extension properties here?
        await this._saveCheckoutDetails().catch(error => {
            this.logMessage('saveCheckoutDetails failed');
            this.props.telemetry.error('There was an error saving your details, please try again later', PaymentMethods.CreditCard);
            this.setState({
              showSuccess: false,
              isBusy: false,
              isProcessingOrder: false
            });
            throw error;
          });

        const { OrderNumber } = this.props.data.checkout.result!.checkoutCart.cart;

        // validation of cc form
        await this.getCCTransactionId(OrderNumber!).then((result) => {
            this.logMessage(`getCCTransactionId result ${OrderNumber} ${JSON.stringify(result)}`);
            if (this.validateCreditcard()) {
                this.postCCPaymentAcceptPage(); // do the thing
            } else {
                this.props.telemetry.error(`CC does not validate`);
                this.displayCheckoutError(this.getCCErrorText(), PaymentMethods.CreditCard);
                return this.setState({
                    isBusy: false,
                    isProcessingOrder: false
                });
            }
        }).catch(() => {
            this.props.telemetry.error(`CC get transaction failed`);
            this.displayCheckoutError(this.props.config.ccPaymentFailed, PaymentMethods.CreditCard);
            return this.setState({
                isBusy: false,
                isProcessingOrder: false
            });
        });
    };

    /**
     * get transaction Id from retail proxy file
     * return Promise Object
     */
    private getCCTransactionId = async (OrderNumber: string): Promise<IGetTransactionIdEntity> => {
        const balanceDue = this.getAmountDue.toFixed(2);
        const returnUrl = this.getCittaPaymentReturnUrl('CC');
        this.logMessage(`Creating sessionId - ReturnUrl: ${returnUrl} amountDue: ${balanceDue}
            OrderNumber ${OrderNumber}
            Get Currency ${this.getCurrency}
            routes: ${JSON.stringify(this.props.context.actionContext.requestContext.app.routes)}
            sitePath: ${this.props.context.actionContext.requestContext.sitePath}
            page url: ${window.location.href}
            location.pathname: ${window.location.pathname}
        `);
        const actionInput = new paymentExpressInput(OrderNumber, balanceDue, this.getCurrency, returnUrl);
        const getTransactionIdResponse = await getTransactionIdAction(actionInput, this.props.context.actionContext).catch(error => {
            this.logError(error, 'getTransactionIdAction failed');
            this.displayCheckoutError(this.props.config.ccPaymentFailed, PaymentMethods.CreditCard);
            this.setState({
                isBusy: false,
                isProcessingOrder: false
            });
            throw error;
        });

        this.logMessage(`getTransactionIdResponse result ${JSON.stringify(getTransactionIdResponse)}`);
        this.setState({
            ccSessionId: getTransactionIdResponse.SessionId
        });
        return getTransactionIdResponse;
    }

    /**
     * Gets credit card error text according to incomplete or invalid fields
     */
    private getCCErrorText = (): string => {
        if (this.state.cardNameError) {
            return this.props.config.completeAllFields || 'Please complete all fields before continuing';
        }

        const errorCount = (this.state.cardNameError ? 1 : 0)
            + (this.state.cardNumError ? 1 : 0)
            + (this.state.cardCvcError ? 1 : 0)
            + (this.state.cardExpiryError ? 1 : 0);
        if (errorCount > 1) {
            return 'Multiple fields are invalid';
        }

        if (this.state.cardNumError) {
            return 'Card number is invalid';
        }
        if (this.state.cardCvcError) {
            return 'Card CVC is invalid';
        }
        if (this.state.cardExpiryError) {
            return 'Card expiry is invalid';
        }
        return this.props.config.completeAllFields || 'Please complete all fields before continuing';
    }

    /**
     * validate credit card number, expirty date, CVC
     * return boolean
     */
    private validateCreditcard = (): boolean => {
        let pass = true;
        if (this.state.ccName === '') {
            this.setState({ cardNameError: true });
            pass = false;
        } else {
            this.setState({ cardNameError: false });
        }

        if (!(this.state.ccNumber && this.state.ccNumber.length > 0)) {
            this.setState({ cardNumError: true });
            pass = false;
        } else {
            this.setState({ cardNumError: false });
        }

        if (validateCVC(this.state.ccCvc) === false) {
            this.setState({ cardCvcError: true });
            pass = false;
        } else {
            this.setState({ cardCvcError: false });
        }

        if (validateCardExpiry(this.state.ccExpiryMonth, this.state.ccExpiryYear) === false) {
            this.setState({ cardExpiryError: true });
            pass = false;
        } else {
            this.setState({ cardExpiryError: false });
        }

        return pass;
    };

    /**
     * post credit card information into payment-express service
     * return null
     */
    private postCCPaymentAcceptPage = (): void => {
        this.logMessage('redirect to Payment Express');
        const ccFormButton = document.getElementById('ccButton') as HTMLElement;
        ccFormButton.click();
    }

    /**
     * Enables and highlights the Payment message block and displays a message
     * @param message the message to display
     *
     * @param paymentMthd the payment message on which to display this error - null for all methods
     */
    private displayCheckoutError = (message: string, paymentMthd?: PaymentMethods) => {
        showPaymentError(true, message, paymentMthd);
        this.setState({
            isProcessingOrder: false,
            isBusy: false
        });
    }

    private createLogMessage = (message: string): string => {
        const { Id, OrderNumber, TotalItems } = this.props.data.checkout!.result!.checkoutCart.cart;
        const url: string = decodeURI(window.location.search);
        const urlParams = new URLSearchParams(url);
        const redirectCartId = urlParams.has('cartid') ? urlParams.get('cartid') : 'N/A';
        const origin = window.location.origin;

        const logMessage = `Message: - ${message} - Origin: ${origin} - CartId: ${Id} - RedirectCartId: ${redirectCartId} - ChannelRefId: ${OrderNumber || 'N/A'} - TotalItem: ${TotalItems || 'N/A'}`;

        return logMessage;
    }

    /**
     * Logs a message to the MSFT logging system
     */
    private logMessage = (message: string): void => {
        const logMessage = this.createLogMessage(message);
        this.cittaTelemetry.trace(logMessage);
        this.props.context.telemetry.error(logMessage);
    }

    /**
    * Logs a message to the MSFT logging system
    */
    private logError = (error: Error, message: string): void => {
        const logMessage = this.createLogMessage(message);
        this.cittaTelemetry.trace(logMessage);
        this.cittaTelemetry.exception(error, logMessage);
        this.props.context.telemetry.error(logMessage);
    }

    /**
     * Methods to calculate remaining Amount Due
     * @param actionInput
     */
    private computeGiftCardAmount = (): number => {
        const {
            data: { checkout }
        } = this.props;
        if (!checkout.result || !checkout.result.checkoutCart.cart) {
            return 0;
        }
        const giftCardCounter = (count: number, giftCard: GiftCard) => {
            return count + (giftCard.Balance || 0);
        };
        const giftCardTotalValue = checkout.result.giftCards.reduce(giftCardCounter, 0);
        const amount = (checkout.result.checkoutCart.cart.TotalAmount || 0) - this.computedLoyaltyAmount();
        return Math.min(giftCardTotalValue, amount);
    };

    private computedLoyaltyAmount = (): number => {
        const checkoutState = this.props.data.checkout.result;
        return (checkoutState && checkoutState.loyaltyAmount) || 0;
    }

    /**
     * Initialises module
     */
    @action
    private init = (): void => {
        // Citta module setup code
        // Load scripts
        this.logMessage('Init modules - citta-checkout-payment-instrument');
        this.setupModule().catch(error => {
            this.logError(error, 'payment module setup failed');
            throw error;
        });
        this.setInitialActiveTab();
    }

    /**
     * Handle payment tab change, credit card, afterpay and laybuy
     */
    // tslint:disable-next-line:no-any
    private handleTabClick = (e: any): void => {
        // if busy - return
        if (this.state.isBusy) {
            return;
        }

        // clear entered credit card data
        this.setState({
            ccNumber: '',
            ccName: '',
            ccExpiryMonth: '01',
            ccExpiryYear: new Date().getFullYear(),
            ccCvc: '',
            ccSessionId: '',
            cardNameError: false,
            cardNumError: false,
            cardCvcError: false,
            cardExpiryError: false
        });

        const oldMthd = paymentStore.paymentMethod;

        const val = e.target.value;
        switch (val) {
            case 'ap': {
                paymentStore.paymentMethod = PaymentMethods.Afterpay;
                break;
            }
            case 'lb': {
                paymentStore.paymentMethod = PaymentMethods.Laybuy;
                break;
            }
            default: {
                paymentStore.paymentMethod = PaymentMethods.CreditCard;
            }
        }

        // if tab changed, clear error message
        if (oldMthd !== paymentStore.paymentMethod) {
            showPaymentError(false);
        }
    };

    /**
     * Allows changing payment tabs with keyboard
     */
    // tslint:disable-next-line:no-any
    private handleKeyDown = (e: any): void => {
        // if busy - return
        if (this.state.isBusy) {
            return;
        }

        if (e.keyCode === KeyCodes.Enter) {
            const val = e.target && e.target.children && e.target.children.length > 0 && e.target.children[0].value;
            switch (val) {
                case 'ap': {
                    paymentStore.paymentMethod = PaymentMethods.Afterpay;
                    break;
                }
                case 'lb': {
                    paymentStore.paymentMethod = PaymentMethods.Laybuy;
                    break;
                }
                default: {
                    paymentStore.paymentMethod = PaymentMethods.CreditCard;
                }
            }
        }
    };

    /**
     * Places order with AX backend
     */
    private checkout = async (checkoutDetailsObject: ICheckoutDetailsObject = {}): Promise<void> => {
        const { context } = this.props;
    
        this.logMessage('Checkout function called');

        // It is placing order
        if (this.state.isBusy) {
            this.logMessage('Checkout is already busy');
            return;
        }

        this.setState({
            isBusy: true,
            isProcessingOrder: true
        });
 
        // Proceed checkout
        const salesOrder: SalesOrder = await placeOrderAction(context.actionContext, checkoutDetailsObject, this.ourGiftToYou).catch(error => {
            this.setState({
                isProcessingOrder: false,
                isBusy: false
            });
            this.logError(error, `Place Order action failed: ${JSON.stringify(error)}`);
            this.displayCheckoutError(`Place Order failed: ${error}`);
            throw error;
        });

        this.logMessage('PlaceOrder end');

        // Remove purchased items from the active cart
        await emptyActiveCart(context.actionContext).catch(error => {
            this.logError(error, 'emptyActiveCartAction failed');
        });

        // Redirect to the order confirmation page
        this.logMessage('Starting redirectToOrderConfirmationPage');
        await this.redirectToOrderConfirmationPage(salesOrder);
    };

    /**
     * Redirects to order confirmation page
     */
    private redirectToOrderConfirmationPage = async (salesOrder: SalesOrder): Promise<void> => {
        const orderConfirmationUrl = getUrlSync('orderConfirmation', this.props.context.actionContext) || '';
        if (!orderConfirmationUrl) {
            this.logMessage('No order confirmation url');
            return;
        }
        const separator = orderConfirmationUrl.includes('?') ? '&' : '?';
        const url = `${orderConfirmationUrl}${separator}transactionId=${salesOrder.Id}&onum=${salesOrder.ChannelReferenceId}`;
        window.location.assign(url);
    };
}

/**
 * Payment methods enum
 */
export enum PaymentMethods {
    CreditCard = 'CreditCard',
    Afterpay = 'Afterpay',
    Laybuy = 'Laybuy'
}

export interface IAfterpayConfigObject {
    type: string | undefined;
    minimumAmount: {
        amount: number;
        currency: string;
    };
    maximumAmount: {
        amount: number;
        currency: string;
    };
}

export default withVisibilityObserver(CheckoutPaymentInstrument);
